import { memo, useCallback, useMemo, useEffect, useState, useRef } from "react";
import { NodeProps, useReactFlow, Handle, Position, ResizeDragEvent, useUpdateNodeInternals, Node, NodeChange, NodeDimensionChange, NodePositionChange, applyNodeChanges } from "reactflow"; // Added NodeChange types and applyNodeChanges
import { NodeResizer } from "@reactflow/node-resizer"; // Import NodeResizer
import { GroupNodeData } from './types';
import { HandleConfig } from '../../types';
import "@reactflow/node-resizer/dist/style.css";
import "./GroupNode.css";
import useStore from "../../store/useStore";
import { Layers } from "lucide-react";
import { EXTENDED_ICON_MAP } from "../../config/icons";
import { isValidHex } from "../../config/colors";

// Helper function to convert hex to RGBA
const hexToRgba = (hex: string, alpha: number): string => {
  const bigint = parseInt(hex.slice(1), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Helper function to get contrasting text color (black or white) for a given hex background
const getContrastingTextColor = (hexColor: string | undefined, isDarkMode: boolean): string => {
  if (!hexColor || !isValidHex(hexColor)) {
    return isDarkMode ? '#FFFFFF' : '#000000'; // Default text color based on theme
  }
  const hex = hexColor.startsWith("#") ? hexColor.slice(1) : hexColor;
  if (hex.length !== 6 && hex.length !== 3) {
      return isDarkMode ? '#FFFFFF' : '#000000';
  }
  let rVal, gVal, bVal;
  if (hex.length === 3) {
    rVal = parseInt(hex[0] + hex[0], 16);
    gVal = parseInt(hex[1] + hex[1], 16);
    bVal = parseInt(hex[2] + hex[2], 16);
  } else {
    rVal = parseInt(hex.slice(0, 2), 16);
    gVal = parseInt(hex.slice(2, 4), 16);
    bVal = parseInt(hex.slice(4, 6), 16);
  }

  const luminance = (0.299 * rVal + 0.587 * gVal + 0.114 * bVal) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

// Define default and minimum sizes/paddings
const DEFAULT_WIDTH = 160;
const DEFAULT_HEIGHT = 120;
const MIN_ABSOLUTE_WIDTH = 20; // Absolute minimum width for a group
const MIN_HEADER_HEIGHT_CONST = 40; // Approximate height of the header (p-2 + icon + p-2)
const MIN_CONTENT_AREA_HEIGHT_BELOW_HEADER = 40; // Minimum space for content below header
const GROUP_NODE_INTERNAL_PADDING = 8; // Padding around child nodes within the content area

function GroupNode({
  data,
  selected,
  id,
  // xPos, yPos are part of the node object from useReactFlow().getNode(id)
}: NodeProps<GroupNodeData>) {
  const { setNodes, getNode } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const hoveredGroupId = useStore((state) => state.hoveredGroupId);
  const allNodes = useStore((state) => state.nodes); // For child calculations

  const [currentIsDarkMode, setCurrentIsDarkMode] = useState(document.documentElement.classList.contains('dark'));
  const headerRef = useRef<HTMLDivElement>(null);
  const iconContainerRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLHeadingElement>(null);
  const customLabelRef = useRef<HTMLSpanElement>(null);
  const [actualHeaderHeight, setActualHeaderHeight] = useState(MIN_HEADER_HEIGHT_CONST); // State for actual header height

  const node = getNode(id);
  const currentWidth = node?.width ?? DEFAULT_WIDTH;
  const currentHeight = node?.height ?? DEFAULT_HEIGHT;

  // Effect to listen for dark mode changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setCurrentIsDarkMode(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Effect to update node internals when handles change
  useEffect(() => {
    if (data.handles) {
      updateNodeInternals(id);
    }
  }, [id, data.handles, updateNodeInternals]);

  // Effect to calculate minimum header width and actual header height
  useEffect(() => {
    if (headerRef.current) {
      let currentIntrinsicWidth = 0;
      let visibleElementsCount = 0;

      if (iconContainerRef.current) {
        currentIntrinsicWidth += iconContainerRef.current.offsetWidth;
        visibleElementsCount++;
      }

      if (nameRef.current) {
        if (visibleElementsCount > 0 && iconContainerRef.current) { // If icon was present before name
          currentIntrinsicWidth += parseFloat(getComputedStyle(nameRef.current).marginLeft);
        }
        currentIntrinsicWidth += nameRef.current.offsetWidth;
        visibleElementsCount++;
      }

      if (customLabelRef.current) {
        if (visibleElementsCount > 0) { // If icon or name was present before custom label
          // ml-auto will take up remaining space. For min-width, consider its own width + a small gap.
          currentIntrinsicWidth += 8; // Arbitrary minimal gap (e.g., 0.5rem for spacing)
        }
        currentIntrinsicWidth += customLabelRef.current.offsetWidth;
      }

      // Header height can still be reliably taken from headerRef itself
      const newActualHeaderHeight = headerRef.current.offsetHeight || MIN_HEADER_HEIGHT_CONST;
      setActualHeaderHeight(newActualHeaderHeight);
    }
  }, [id, data.name, data.customLabel, data.icon, data.iconVisibility, data.groupNameVisibility, data.customLabelVisibility, currentIsDarkMode]); // Added currentIsDarkMode

  // Memoize children of this group
  const childrenOfThisGroup = useMemo(() => {
    return allNodes.filter((n: Node) => n.parentId === id);
  }, [allNodes, id]);

  // Define minimum dimensions for NodeResizer
  const minResizeWidth = MIN_ABSOLUTE_WIDTH; // Allow resizing to absolute minimum width
  const currentMinResizeHeight = useMemo(() => {
    const height = childrenOfThisGroup.length === 0
      ? actualHeaderHeight // Just the header height if no children
      : actualHeaderHeight + MIN_CONTENT_AREA_HEIGHT_BELOW_HEADER + (GROUP_NODE_INTERNAL_PADDING * 2); // Header + min content area + padding for content
    return height;
  }, [id, childrenOfThisGroup.length, actualHeaderHeight]); // id was already here


  const isHoverTarget = hoveredGroupId === id;

  const getIconComponent = useCallback(() => {
    return EXTENDED_ICON_MAP[data.icon] || Layers;
  }, [data.icon]);

  const IconComponent = getIconComponent();

  const handles = useMemo(() => (data.handles ? [...data.handles] : []), [data.handles]);

  const handlesByPosition = useMemo(() => {
    return handles.reduce((acc, handle) => {
      if (!acc[handle.position]) acc[handle.position] = [];
      acc[handle.position].push(handle);
      return acc;
    }, {} as Record<Position, HandleConfig[]>);
  }, [handles]);

  const onResize = useCallback((_event: ResizeDragEvent, params: { width: number; height: number; x: number; y: number; direction: number[] }) => {
    const { width: newWidth, height: newHeight, x: newAbsX, y: newAbsY } = params; // Removed direction
    const groupNode = getNode(id);

    if (!groupNode) return;

    const changes: NodeChange[] = [];

    // Update group node's position and dimensions
    // If the node is being moved (newAbsX or newAbsY is different from current position)
    // or resized (newWidth or newHeight is different from current dimensions)
    if (groupNode.position.x !== newAbsX || groupNode.position.y !== newAbsY) {
      changes.push({
        id: id,
        type: 'position',
        position: { x: newAbsX, y: newAbsY },
      } as NodePositionChange);
    }

    if (groupNode.width !== newWidth || groupNode.height !== newHeight) {
      changes.push({
        id: id,
        type: 'dimensions',
        dimensions: { width: newWidth, height: newHeight },
        resizing: true,
      } as NodeDimensionChange);
    }
    
    // Apply changes only if there are any
    if (changes.length > 0) {
      setNodes((nds) => applyNodeChanges(changes, nds));
    }
  }, [id, setNodes, getNode]); // Removed allNodes as children are no longer repositioned

  const groupStyle = useMemo(() => {
    const baseStyle: React.CSSProperties = {};
    if (data.color && data.color !== "Default" && isValidHex(data.color)) {
      const opacity = data.colorOpacity ?? 0.3; // Default to 30% opacity if not set
      // Use full opacity range for background
      baseStyle.backgroundColor = hexToRgba(data.color, opacity);
      
      // Ensure border is visible at low opacity values
      // For opacity < 0.2, use a light-colored border that's visible
      // For opacity >= 0.2, use the color with appropriate opacity
      const borderOpacity = opacity < 0.2 
        ? 0.3 // Minimum border opacity for visibility
        : Math.max(opacity, 0.3); // Never go below 30% for border
        
      baseStyle.borderColor = hexToRgba(data.color, borderOpacity);
      
      // Add a light-colored border for very low opacity values
      if (opacity < 0.1) {
        baseStyle.borderWidth = '1px';
        baseStyle.borderStyle = 'dashed';
      }
    } else { // Handles undefined, "Default", or invalid hex
      if (currentIsDarkMode) {
        baseStyle.backgroundColor = 'rgba(255, 255, 255, 0.03)';
        baseStyle.borderColor = 'rgba(255, 255, 255, 0.07)';
      } else {
        baseStyle.backgroundColor = 'rgba(255, 255, 255, 0.5)';
        baseStyle.borderColor = 'rgba(15, 13, 24, 0.15)';
      }
    }
    return baseStyle;
  }, [data.color, data.colorOpacity, currentIsDarkMode]);

  const customLabelTextColor = useMemo(() => {
    return getContrastingTextColor(data.color, currentIsDarkMode);
  }, [data.color, currentIsDarkMode]);

  const customLabelStyle = useMemo(() => {
    const style: React.CSSProperties = {};
    if (data.color && data.color !== "Default" && isValidHex(data.color) && data.customLabelVisibility !== "hidden" && data.customLabel) {
        const pickerOpacity = data.colorOpacity ?? 1;
        // Ensure label background has some visibility, e.g., min 10% opacity, or picker-driven opacity
        const labelBgOpacity = Math.max(0.3, pickerOpacity * 0.6);
        style.backgroundColor = hexToRgba(data.color, labelBgOpacity);
        style.color = customLabelTextColor;
    } else if (data.customLabelVisibility !== "hidden" && data.customLabel) { // Handles "Default", undefined, or invalid hex
        // Fallback to default theme-based colors if no data.color (approximating original Tailwind)
        style.backgroundColor = currentIsDarkMode ? 'rgba(30, 58, 138, 0.5)' : 'rgba(219, 234, 254, 1)'; // dark:bg-blue-900/50 or bg-blue-100
        style.color = currentIsDarkMode ? 'rgba(191, 219, 254, 1)' : 'rgba(30, 64, 175, 1)'; // dark:text-blue-200 or text-blue-800
    }
    // Add padding directly here as Tailwind classes for bg/text are removed
    style.paddingLeft = '0.5rem'; // px-2
    style.paddingRight = '0.5rem'; // px-2
    style.paddingTop = '0.125rem'; // py-0.5
    style.paddingBottom = '0.125rem'; // py-0.5
    return style;
  }, [data.color, data.colorOpacity, data.customLabel, data.customLabelVisibility, currentIsDarkMode, customLabelTextColor]);

  return (
    <div
      style={{
        width: currentWidth,
        height: currentHeight,
        position: 'relative',
      }}
      className="group-node-wrapper"
    >
      {selected && (
        <NodeResizer
          minWidth={minResizeWidth}
          minHeight={currentMinResizeHeight} // Use the dynamic min height
          isVisible={selected} // Show when selected
          onResize={onResize}
          keepAspectRatio={false} // Allow freeform resizing
          handleStyle={{
            width: '8px',
            height: '8px',
            backgroundColor: '#3b82f6', // blue-500
            border: '1px solid white',
            borderRadius: '2px', // Square-ish handles
          }}
          lineStyle={{
            borderColor: '#3b82f6',
            borderWidth: '1px',
          }}
        />
      )}

      <div
        style={groupStyle}
        className={`
          absolute inset-0 rounded-lg transition-colors duration-100 ease-in-out h-full w-full cursor-pointer
          border shadow-sm dark:shadow-sm-dark
          ${selected ? 'ring-2 ring-blue-500/50' : ''}
          ${isHoverTarget ? 'ring-2 ring-blue-500 border-blue-500 bg-blue-50/30 dark:bg-blue-900/30' : ''}
        `}
      >
        <div className="flex flex-col h-full">
          <div ref={headerRef} className="flex items-center p-2 flex-shrink-0">
            {data.iconVisibility !== "hidden" && (
              <div ref={iconContainerRef} className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 bg-white text-gray-600 dark:bg-gray-950/50 dark:text-gray-300">
                <IconComponent size={16} />
              </div>
            )}

            {data.groupNameVisibility !== "hidden" && (
              <h3 ref={nameRef} className={`font-semibold text-sm text-gray-900 dark:text-gray-100 truncate min-w-0 text-left ${data.iconVisibility !== "hidden" ? 'ml-2' : ''}`}>
                {data.name}
              </h3>
            )}

            {data.customLabelVisibility !== "hidden" && data.customLabel && (
              <span ref={customLabelRef}
                className="ml-auto inline-flex items-center rounded text-xs font-medium truncate max-w-[50%]" // Removed color and padding Tailwind classes
                style={customLabelStyle}
              >
                {data.customLabel}
              </span>
            )}
          </div>

          <div className="flex-grow relative min-h-0">
            {/* Child nodes are rendered here by React Flow */}
          </div>
        </div>
      </div>

      {Object.entries(handlesByPosition).map(([_, positionHandles]) =>
        positionHandles.map((handle, index) => (
          <Handle
            key={`${handle.id}-${handle.color}`}
            id={handle.id}
            type={handle.type}
            position={handle.position as Position}
            className={`!w-3 !h-3 !border-2 !border-white dark:!border-gray-700`}
            style={{
              ...calculateHandleStyle(index, positionHandles.length, handle.position),
              backgroundColor: handle.color,
            }}
          />
        ))
      )}
    </div>
  );
}

const calculateHandleStyle = (index: number, total: number, handlePosition: Position): React.CSSProperties => {
  const calcOffset = 5;
  const percentage = (index + 1) * (100 / (total + 1));
  switch (handlePosition) {
    case Position.Top:
      return { left: `calc(${percentage}% - ${calcOffset}px)`, top: `-${calcOffset}px` };
    case Position.Bottom:
      return { left: `calc(${percentage}% - ${calcOffset}px)`, bottom: `-${calcOffset}px` };
    case Position.Left:
      return { top: `calc(${percentage}% - ${calcOffset}px)`, left: `-${calcOffset}px` }; // Added left offset
    case Position.Right:
      return { top: `calc(${percentage}% - ${calcOffset}px)`, right: `-${calcOffset}px` }; // Added right offset
    default:
      return { top: `calc(${percentage}% - ${calcOffset}px)` };
  }
};

export default memo(GroupNode);
